<script setup>
import {
  computed,
  ref
} from 'vue'
import { useStore } from 'vuex'
import {
  useRoute,
} from 'vue-router'
import { useI18n } from 'vue-i18n'
import moment from 'moment'
import { dayOfWeek } from '../../../../modules/dayOfWeekConst'

import {
  KkBasicInfoEditModal,
  KkStatusMessage,
  KkSnsServiceIconList,
  KkCategoryTip,
  KkActionButtonList,
  KkSnsPostContent,
  KkAccountsModal,
  KkActionModal
} from '../../../../components/domains/sns'

const store = useStore()
const { t } = useI18n()

const props = defineProps({
  id: {
    type: Number,
    required: true
  },
  status: {
    type: String,
    default: ''
  },
  category: {
    type: Object,
    default: () => {},
  },
  title: {
    type: String,
    default: '',
  },
  images: {
    type: Array,
    default: () => []
  },
  movie: {
    type: Object,
    default: () => {}
  },
  publishedAt: {
    type: Date,
    require: false
  },
  scheduledAt: {
    type: Date,
    require: false
  },
  postApprovalFlow: {
    type: Boolean,
    default: false,
  },
  approverName: {
    type: String,
    default: ''
  },
  applicantName: {
    type: String,
    default: ''
  },
  authorName: {
    type: String,
    require: true
  },
  targets: {
    type: Array,
    default: () => []
  },
  selectedPostStatusType: {
    type: String,
    default: ''
  }
})
const emit = defineEmits(['delete-post'])

const showMoreThanOperatorAuthority = computed(() => store.getters['companiesAccessibleApi/showMoreThanOperatorAuthority'])
const showOnlyOwnerAuthority = computed(() => store.getters['companiesAccessibleApi/showOnlyOwnerAuthority'])
const showBasicInfoEditModal = ref(false)
const showSnsAccountModal = ref(false)
const showPreviewModal = ref(false)
const { company_uuid } = useRoute().params
const getPost = (payload) => store.dispatch('companiesStoreSnsPosts/getPost', payload)

const publishedDateTime = computed(() => {
  if (!props.publishedAt && !props.scheduledAt) return ''

  const dateTime = props.publishedAt ? props.publishedAt : props.scheduledAt
  const dayOfWeek = dayOfWeekOfReservationDate.value.charAt(0)
  return moment(dateTime).format(`${t('format.date.default')}(${dayOfWeek}) ${t('format.time.default')}`)
})
const dayOfWeekOfReservationDate = computed(() => {
  const dowNumber = moment(props.scheduledAt).day()
  return Object.values(dayOfWeek)[dowNumber]
})

const approver = computed(() => {
  if(props.postApprovalFlow && props.approverName) {
    return t('pages.CompaniesStoreSnsPost.index.approverName', { approver: props.approverName })
  }
  return null
})

const applicant = computed(() => {
  if(props.postApprovalFlow && props.applicantName) {
    return t('pages.CompaniesStoreSnsPost.index.applicantName', { applicant: props.applicantName })
  }
  return null
})


const deletePost = (id) => {
  emit('delete-post', id)
}

const openPreviewModal = () => {
  const payload = {
    companyUuid: company_uuid,
    id: props.id,
    isPreview: true,
  }
  getPost(payload)
  showPreviewModal.value = true
}

const publishedStatus = () => {
  if (!props.publishedAt && !props.scheduledAt) return ''

  return !props.publishedAt && props.scheduledAt.getTime() > new Date().getTime() ? '投稿予約' : ''
}

const canDelete = computed(()=> showMoreThanOperatorAuthority.value)

const canEdit = computed(()=> {
  if(!showMoreThanOperatorAuthority.value) {
    return false
  }

  // 投稿予約時間を過ぎるまでは編集可能
  if(props.scheduledAt.getTime() - new Date().getTime() > 0) {
    if(props.postApprovalFlow) return showOnlyOwnerAuthority.value

    return true
  }

  return false
})
const postedSns = () => {
  const targetSns = props.targets.map(v => v.kind)
  return [...new Set(targetSns)]
}
</script>

<template>
  <div class="card">
    <div class="card__title">
      <KkCategoryTip :name="props.category.name" :is-editable="props.selectedPostStatusType === 'POST'" @click="showBasicInfoEditModal = !showBasicInfoEditModal" />
      <p v-if="title" class="title">{{ title }}</p>
      <KkBasicInfoEditModal
        :id="props.id"
        :show-modal="showBasicInfoEditModal"
        :category-id="props.category.id"
        :title="props.title"
        @close="showBasicInfoEditModal = false"
      />
    </div>
    <div class="card__content">
      <KkSnsPostContent :images="props.images" :movie="props.movie" height="120px" />
    </div>
    <div class="card__status">
      <div class="published">
        {{ t('pages.CompaniesStoreSnsPost.index.bookingDate') }}：<time class="published__at">{{ publishedDateTime }}</time>
      </div>
      <template v-if="publishedStatus() === '投稿予約'" class="reserving">
        <span class="reserving__icon" :class="{ future: publishedStatus() === '投稿予約' }">
          {{ publishedStatus() }}
        </span>
        <span class="reserving__text">予約時間に投稿されます</span>
      </template>
      <template v-else>
        <KkStatusMessage v-if="props.status" :status="props.status" />
      </template>
      <div v-if="postApprovalFlow && approverName" class="stakeholder">
        {{ approver }}
      </div>
      <div v-if="postApprovalFlow && applicantName" class="stakeholder">
        {{ applicant }}
      </div>
      <div class="stakeholder">
        {{ t('pages.CompaniesStoreSnsPost.index.authorName', { author: props.authorName }) }}
      </div>
    </div>
    <div class="card__setting">
      <KkSnsServiceIconList :posted-sns="postedSns()" />
      <button
        type="button"
        class="account"
        @click="showSnsAccountModal = !showSnsAccountModal"
      >
        {{ props.targets.length }}アカウント
      </button>
      <KkAccountsModal
        v-if="showSnsAccountModal"
        :targets="props.targets"
        :scheduled-at="props.scheduledAt"
        :status="props.status"
        :open-modal="showSnsAccountModal"
        @close="showSnsAccountModal = false"
      />
      <KkActionModal
        v-if="showPreviewModal"
        :is-preview="true"
        :show-action-modal="showPreviewModal"
        @close-action-modal="showPreviewModal = false"
      />
    </div>
    <KkActionButtonList
      :show-edit="canEdit"
      :route-parameter-edit="{ name: 'CompaniesStoreSnsPostsEdit', params: { company_uuid: company_uuid, post_id: id } }"
      :show-preview="true"
      :show-copy="showMoreThanOperatorAuthority"
      :route-parameter-copy="{ name: 'CompaniesStoreSnsPostsNew', params: { company_uuid: company_uuid}, query: { copy: id } }"
      :show-delete="canDelete"
      @clickPreviewButton="openPreviewModal"
      @clickDeleteButton="deletePost(props.id)"
    />
  </div>
</template>

<style lang="scss" scoped>
@import "../../../../../../stylesheets/biz_spa/foundation/_var.scss";
.card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  padding: 24px;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
  &__title {
    width: 40%;
    margin-right: 40px;
    @media screen and (max-width: 768px) {
      width: 100%;
      margin: 16px 0 0;
    }
    .title {
      margin-bottom: 0;
      font-size: 14px;
      white-space: pre-wrap;
      word-break: break-all;
    }
  }
  &__content {
    flex: none;
    width: 20%;
    margin-right: 24px;
    @media screen and (max-width: 768px) {
      width: 100%;
      margin-right: 0;
    }
  }
  &__status {
    flex: none;
    width: 260px;
    margin-right: 40px;
    @media screen and (max-width: 768px) {
      width: 100%;
      margin: 16px 0 0;
    }
    .published {
      display: flex;
      align-items: center;
      font-size: 12px;
      line-height: 1.5;
    }
    .reserving {
      margin-top: 10px;
      &__icon {
        display: inline-flex;
        align-items: center;
        height: 20px;
        padding: 0 6px;
        border-radius: 10px;
        background-color: $color-accent-primary;
        color: #FFF;
        font-weight: bold;
        font-size: 10px;
        line-height: 1;
      }
      &__text {
        margin-left: 8px;
        color: $color-accent-primary;
        font-size: 12px;
      }
    }
    .stakeholder {
      margin-top: 10px;
      font-size: 12px;
    }
  }
  &__setting {
    align-items: center;
    width: 20%;
    margin-right: 40px;
    @media screen and (max-width: 768px) {
      width: 100%;
      margin: 16px 0 0;
    }
    .account {
      align-items: center;
      margin-top: 10px;
      font-size: 12px;
      line-height: 1.5;
      text-decoration: underline;
    }
  }
  &__action {
    display: flex;
    flex: none;
    flex-direction: column;
    align-items: flex-start;
    @media screen and (max-width: 768px) {
      flex-direction: row;
      justify-content: space-between;
      width: 100%;
      margin-top: 16px !important;
    }
    &.c-button {
      margin-top: 0;
    }
  }
}
</style>
